import * as THREE from 'three';
import { POST } from '../config/tuning';
import { bus } from '../core/EventBus';
import {
  adaptExposureStops,
  createDownlinkArtefacts,
  createDownlinkFrame,
  decodeMeterByte,
  downlinkArtefacts,
  downlinkFrame,
  grainGainFromStops,
  rollingShutterSkew,
  targetExposureStops,
  type DownlinkArtefacts,
  type DownlinkFrame,
} from './PostDriver';
import { anyEffectEnabled, createPostSettings, type PostSettings } from './PostSettings';
import {
  FULLSCREEN_VERTEX,
  METER_DOWNSAMPLE_FRAGMENT,
  METER_REDUCE_FRAGMENT,
  OPTICS_FRAGMENT,
  SIGNAL_FRAGMENT,
  TRANSMIT_FRAGMENT,
} from './shaders/post.glsl';

/**
 * The video downlink post-processing stack (SPEC §3.3.2).
 *
 * Everything the player sees passes through here, which is what makes the game read as a video
 * feed from a drone rather than as a rendered scene. Two of the ten effects are gameplay systems
 * rather than decoration: auto-exposure, because a sun-facing ingress genuinely hides threats, and
 * compression blocking, because a picture breaking up is the only warning that the link is being
 * jammed. Both are driven from the simulation, not from a clock.
 *
 * **Structure.** The scene renders into a half-float target and is then walked through at most
 * three full-screen passes (see shaders/post.glsl.ts for what each one does and why they are
 * grouped that way), ping-ponging between two intermediates. A pass whose every effect is
 * switched off is skipped entirely rather than run as an identity, so the accessibility toggles
 * buy back real frame time; with all of them off, `Renderer` bypasses the stack altogether.
 *
 * **Held frames.** The scene target ping-pongs too, so the previous frame is still intact. A
 * downlink frame hold is then a texture swap — bind last frame's target as the source — rather
 * than a blend inside the shader, which would have doubled every tap in the heaviest pass.
 *
 * **Metering.** Exposure is measured on the GPU (scene → 16×16 → 1×1 of log luminance) and read
 * back to the CPU as a single byte, 15 times a second. The readback stalls the pipeline, which is
 * why it is rate-limited; the CPU has to see the number regardless, because the HUD must be able
 * to display the gain state when the effect itself is switched off (SPEC §3.3.2).
 *
 * Units: pixels are device pixels unless suffixed; angles radians; times seconds.
 */

/** What the simulation tells the stack each rendered frame. */
export interface PostDrive {
  /** Render frame time, seconds. This is presentation, so it runs on the render clock. */
  dtS: number;
  /** Simulated time, seconds. Drives every artefact, so the same flight breaks up the same way. */
  simTimeS: number;
  /** 0..1 (SPEC §3.3.2 item 6). The single number the whole breakdown hangs off. */
  linkQuality: number;
  /** Yaw rate, rad/s — the rolling-shutter skew. */
  yawRateRadS: number;
  /** Camera horizontal field of view, radians. */
  horizontalFovRad: number;
  /** Unit vector toward the sun, world space. */
  sunDirection: THREE.Vector3;
  /** Haze tint, matching the sky's horizon colour. */
  hazeColour: THREE.Color;
  /** True when the aircraft is over the sea, where the air is wetter and the veil thicker. */
  overWater: boolean;
  /** Camera height above sea level, metres — the haze veil's path length comes off it. */
  altitudeM: number;
}

const cameraRight = new THREE.Vector3();
const cameraUp = new THREE.Vector3();
const cameraForward = new THREE.Vector3();

export class PostStack {
  readonly settings: PostSettings = createPostSettings();

  private readonly quadScene = new THREE.Scene();
  private readonly quadCamera = new THREE.Camera();
  private readonly quad: THREE.Mesh;
  private readonly quadGeometry = new THREE.PlaneGeometry(2, 2);

  private readonly opticsMaterial: THREE.ShaderMaterial;
  private readonly signalMaterial: THREE.ShaderMaterial;
  private readonly transmitMaterial: THREE.ShaderMaterial;
  private readonly meterDownsampleMaterial: THREE.ShaderMaterial;
  private readonly meterReduceMaterial: THREE.ShaderMaterial;
  private readonly copyMaterial: THREE.ShaderMaterial;

  /** Scene targets, ping-ponged so the previous frame survives for a hold. */
  private readonly sceneTargets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private readonly chainTargets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private readonly meterGridTarget: THREE.WebGLRenderTarget;
  /** Ping-ponged: the frame written this interval is read back the *next* one. See `meter`. */
  private readonly meterSingleTargets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private readonly meterPixel = new Uint8Array(4);
  private meterIndex = 0;
  private meterPrimed = false;

  private readonly artefacts: DownlinkArtefacts = createDownlinkArtefacts();
  private readonly downlink: DownlinkFrame = createDownlinkFrame();

  private sceneIndex = 0;
  private widthPx = 1;
  private heightPx = 1;

  private exposureStopsValue = 0;
  private measuredLuminanceValue: number = POST.exposureTargetLuminance;
  private exposureSettled = false;
  private meterAccumulatorS = 0;
  private linkQualityValue = 1;
  private lastDrawMsValue = 0;

  constructor() {
    this.sceneTargets = [this.createSceneTarget(), this.createSceneTarget()];
    this.chainTargets = [this.createChainTarget(), this.createChainTarget()];
    this.meterGridTarget = this.createMeterTarget(POST.meterResolution);
    this.meterSingleTargets = [this.createMeterTarget(1), this.createMeterTarget(1)];

    this.opticsMaterial = this.createMaterial(OPTICS_FRAGMENT, {
      uTexel: { value: new THREE.Vector2(1, 1) },
      uAspect: { value: 1 },
      uUseDistortion: { value: 1 },
      uUseChromatic: { value: 1 },
      uUseHaze: { value: 1 },
      uUseVignette: { value: 1 },
      uUseFxaa: { value: 1 },
      uBarrelK1: { value: POST.barrelK1 },
      // The corner maps exactly to the corner, so the crop is the smallest that avoids sampling
      // outside the frame: 1/(1 + k1), about 11% at k1 = 0.12.
      uBarrelScale: { value: 1 / (1 + POST.barrelK1) },
      uInvCornerR2: { value: 1 },
      uChromatic: { value: new THREE.Vector2() },
      uSkew: { value: 0 },
      uJitter: { value: new THREE.Vector2() },
      uTearY: { value: 1 },
      uTearShift: { value: 0 },
      uHazeColour: { value: new THREE.Color(0.6, 0.7, 0.76) },
      uHazeVeil: { value: POST.hazeVeilStrength },
      uHazeDensity: { value: POST.hazeDensity },
      uHazeMaxRange: { value: POST.hazeMaxRangeM },
      uCameraAltitudeM: { value: 0 },
      uHazeGlare: { value: POST.hazeSunGlare },
      uHazeAnisotropy: { value: POST.hazeSunAnisotropy },
      uSunDirection: { value: new THREE.Vector3(0, 1, 0) },
      uCameraRight: { value: new THREE.Vector3(1, 0, 0) },
      uCameraUp: { value: new THREE.Vector3(0, 1, 0) },
      uCameraForward: { value: new THREE.Vector3(0, 0, -1) },
      uTanHalfFov: { value: new THREE.Vector2(1, 1) },
      uVignetteStrength: { value: POST.vignetteStrength },
      uVignetteStart: { value: POST.vignetteStart },
    });

    this.signalMaterial = this.createMaterial(SIGNAL_FRAGMENT, {
      uResolution: { value: new THREE.Vector2(1, 1) },
      uUseGrade: { value: 1 },
      uUseGrain: { value: 1 },
      uExposure: { value: 1 },
      uTonemapKnee: { value: POST.tonemapKnee },
      uSaturation: { value: POST.gradeSaturation },
      uContrast: { value: POST.gradeContrast },
      uLift: { value: POST.gradeLift },
      uPivot: { value: POST.gradePivot },
      uShadowTint: { value: new THREE.Vector3(...POST.gradeShadowTint) },
      uHighlightTint: { value: new THREE.Vector3(...POST.gradeHighlightTint) },
      uTintStrength: { value: POST.gradeTintStrength },
      uGrainAmplitude: { value: POST.grainAmplitude },
      uGrainShadowGain: { value: POST.grainShadowGain },
      uGrainGain: { value: 1 },
      uGrainTime: { value: 0 },
    });

    this.transmitMaterial = this.createMaterial(TRANSMIT_FRAGMENT, {
      uUseBlocking: { value: 1 },
      uBlocks: { value: new THREE.Vector2(1, 1) },
      uBlockCoverage: { value: 0 },
      uQuantLevels: { value: POST.quantLevelsClean },
      uBlockSalt: { value: 0 },
    });

    this.meterDownsampleMaterial = this.createMaterial(METER_DOWNSAMPLE_FRAGMENT, {
      uFootprint: { value: new THREE.Vector2(1, 1) },
      uEncodeMin: { value: POST.meterEncodeMinStops },
      uEncodeSpan: { value: POST.meterEncodeMaxStops - POST.meterEncodeMinStops },
    });

    this.meterReduceMaterial = this.createMaterial(METER_REDUCE_FRAGMENT, {
      uCentreWeight: { value: POST.meterCentreWeight },
    });
    this.meterReduceMaterial.defines = { METER_RES: POST.meterResolution };

    // Used only when every effect in every pass is off but the stack is still in the path.
    this.copyMaterial = this.createMaterial(
      'precision highp float;\nuniform sampler2D tDiffuse;\nvarying vec2 vUv;\n' +
        'void main() { gl_FragColor = vec4(texture2D(tDiffuse, vUv).rgb, 1.0); }\n',
      {},
    );

    this.quad = new THREE.Mesh(this.quadGeometry, this.opticsMaterial);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);

    bus.on('render:resize', (payload) => this.resize(payload.widthPx, payload.heightPx));
  }

  /** True when at least one effect is on, so the stack is worth running at all. */
  get enabled(): boolean {
    return anyEffectEnabled(this.settings);
  }

  /** Current camera gain, stops. Positive is gained up — a dark scene lifted and noisy. */
  get exposureStops(): number {
    return this.exposureStopsValue;
  }

  /** Last metered log-average scene luminance, unitless. */
  get measuredLuminance(): number {
    return this.measuredLuminanceValue;
  }

  /**
   * The gain the scene is asking for, stops — what auto-exposure would be applying if it were on.
   *
   * Null while the effect is running (the picture is the readout) or while the stack is bypassed
   * entirely (there is no camera response to report). Otherwise the HUD displays it, because
   * SPEC §3.3.2 requires disabling a gameplay-carrying effect to surface a compensating readout:
   * with auto-exposure off, this is the only way to know the camera is staring into the sun.
   */
  get exposureReadoutStops(): number | null {
    if (this.settings.exposure || !this.enabled) return null;
    return targetExposureStops(this.measuredLuminanceValue);
  }

  /** Link quality the stack last ran at, 0..1. */
  get linkQuality(): number {
    return this.linkQualityValue;
  }

  /** Milliseconds spent in the stack last frame, for the debug overlay's budget line. */
  get lastDrawMs(): number {
    return this.lastDrawMsValue;
  }

  /** Number of full-screen chain passes the current settings will run. */
  get passCount(): number {
    return (this.opticsNeeded ? 1 : 0) + (this.signalNeeded ? 1 : 0) + (this.transmitNeeded ? 1 : 0);
  }

  /** Resize every target to the backing-buffer size, in device pixels. */
  resize(widthPx: number, heightPx: number): void {
    const width = Math.max(1, Math.floor(widthPx));
    const height = Math.max(1, Math.floor(heightPx));
    if (width === this.widthPx && height === this.heightPx) return;
    this.widthPx = width;
    this.heightPx = height;

    this.sceneTargets[0].setSize(width, height);
    this.sceneTargets[1].setSize(width, height);
    this.chainTargets[0].setSize(width, height);
    this.chainTargets[1].setSize(width, height);

    const aspect = width / height;
    const optics = this.opticsMaterial.uniforms;
    (optics['uTexel'].value as THREE.Vector2).set(1 / width, 1 / height);
    optics['uAspect'].value = aspect;
    optics['uInvCornerR2'].value = 1 / (aspect * aspect + 1);
    (optics['uChromatic'].value as THREE.Vector2).set(
      POST.chromaticPx / width,
      POST.chromaticPx / height,
    );

    (this.signalMaterial.uniforms['uResolution'].value as THREE.Vector2).set(width, height);
    (this.transmitMaterial.uniforms['uBlocks'].value as THREE.Vector2).set(
      width / POST.blockSizePx,
      height / POST.blockSizePx,
    );
    (this.meterDownsampleMaterial.uniforms['uFootprint'].value as THREE.Vector2).set(
      1 / POST.meterResolution,
      1 / POST.meterResolution,
    );
  }

  /**
   * Fold this frame's simulation state into the uniforms. Called before `render`, once per
   * rendered frame; allocation-free.
   */
  update(drive: PostDrive): void {
    this.linkQualityValue = drive.linkQuality;

    downlinkArtefacts(drive.linkQuality, this.artefacts);
    if (this.settings.jitter) {
      downlinkFrame(drive.simTimeS, this.artefacts, this.downlink);
    } else {
      this.downlink.hold = false;
      this.downlink.tearY = 1;
      this.downlink.tearShift = 0;
      this.downlink.offsetX = 0;
      this.downlink.offsetY = 0;
    }

    const optics = this.opticsMaterial.uniforms;
    optics['uUseDistortion'].value = this.settings.distortion ? 1 : 0;
    optics['uUseChromatic'].value = this.settings.chromatic ? 1 : 0;
    optics['uUseHaze'].value = this.settings.haze ? 1 : 0;
    optics['uUseVignette'].value = this.settings.vignette ? 1 : 0;
    optics['uUseFxaa'].value = this.settings.fxaa ? 1 : 0;

    optics['uSkew'].value = this.settings.rollingShutter
      ? rollingShutterSkew(drive.yawRateRadS, drive.horizontalFovRad)
      : 0;
    (optics['uJitter'].value as THREE.Vector2).set(this.downlink.offsetX, this.downlink.offsetY);
    optics['uTearY'].value = this.downlink.tearY;
    optics['uTearShift'].value = this.downlink.tearShift;

    (optics['uSunDirection'].value as THREE.Vector3).copy(drive.sunDirection);
    (optics['uHazeColour'].value as THREE.Color).copy(drive.hazeColour);
    optics['uHazeVeil'].value =
      POST.hazeVeilStrength * (drive.overWater ? POST.hazeOverWaterBoost : 1);
    optics['uCameraAltitudeM'].value = Math.max(drive.altitudeM, 1);

    const signal = this.signalMaterial.uniforms;
    signal['uUseGrade'].value = this.settings.grade ? 1 : 0;
    signal['uUseGrain'].value = this.settings.grain ? 1 : 0;
    signal['uGrainGain'].value = grainGainFromStops(this.exposureStopsValue);
    // Stepped, not continuous: grain resampled every frame crawls, which reads as a bug.
    signal['uGrainTime'].value = Math.floor(drive.simTimeS * POST.grainRateHz);

    const transmit = this.transmitMaterial.uniforms;
    transmit['uUseBlocking'].value = this.settings.blocking ? 1 : 0;
    transmit['uBlockCoverage'].value = this.artefacts.blockCoverage;
    transmit['uQuantLevels'].value = this.artefacts.quantLevels;
    transmit['uBlockSalt'].value = Math.floor(drive.simTimeS * POST.linkFrameHz);

    this.meterAccumulatorS += drive.dtS;
    this.exposureStopsValue = this.settings.exposure
      ? adaptExposureStops(
          this.exposureStopsValue,
          targetExposureStops(this.measuredLuminanceValue),
          drive.dtS,
        )
      : 0;
    signal['uExposure'].value = 2 ** this.exposureStopsValue;
  }

  /**
   * Draw the scene through the stack and present it. `update` must have run first.
   *
   * The camera basis is captured here rather than in `update` because the haze pass reconstructs a
   * view ray per pixel from it, and the matrices are only current once the render loop has placed
   * the camera for this frame.
   */
  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera): void {
    this.sceneIndex ^= 1;
    const sceneTarget = this.sceneTargets[this.sceneIndex];
    renderer.setRenderTarget(sceneTarget);
    renderer.render(scene, camera);

    // Timed from here, so `lastDrawMs` is the stack's own cost against the 3 ms budget of M4 and
    // not the scene render it happens to be wrapping.
    const startMs = performance.now();
    this.updateCameraUniforms(camera);
    // Metered even when the effect is switched off: the HUD's compensating gain readout needs the
    // number, and two 16×16 passes are cheaper than explaining why the readout went blank.
    this.meter(renderer, sceneTarget);

    // A held frame shows the previous one; the fresh render still happened, so the next frame has
    // something to hold in turn and the frame time does not lurch when the link recovers.
    const held = this.downlink.hold ? this.sceneTargets[this.sceneIndex ^ 1] : sceneTarget;

    let source = held.texture;
    let chain = 0;
    let remaining = this.passCount;

    if (remaining === 0) {
      this.runPass(renderer, this.copyMaterial, source, null);
    } else {
      if (this.opticsNeeded) {
        remaining -= 1;
        source = this.runPass(renderer, this.opticsMaterial, source, this.nextTarget(remaining, chain));
        chain ^= 1;
      }
      if (this.signalNeeded) {
        remaining -= 1;
        source = this.runPass(renderer, this.signalMaterial, source, this.nextTarget(remaining, chain));
        chain ^= 1;
      }
      if (this.transmitNeeded) {
        remaining -= 1;
        this.runPass(renderer, this.transmitMaterial, source, null);
      }
    }

    renderer.setRenderTarget(null);
    this.lastDrawMsValue = performance.now() - startMs;
  }

  dispose(): void {
    this.sceneTargets[0].dispose();
    this.sceneTargets[1].dispose();
    this.chainTargets[0].dispose();
    this.chainTargets[1].dispose();
    this.meterGridTarget.dispose();
    this.meterSingleTargets[0].dispose();
    this.meterSingleTargets[1].dispose();
    this.quadGeometry.dispose();
    this.opticsMaterial.dispose();
    this.signalMaterial.dispose();
    this.transmitMaterial.dispose();
    this.meterDownsampleMaterial.dispose();
    this.meterReduceMaterial.dispose();
    this.copyMaterial.dispose();
  }

  // --- Internals -------------------------------------------------------------------------------

  private get opticsNeeded(): boolean {
    const s = this.settings;
    return s.distortion || s.chromatic || s.haze || s.vignette || s.fxaa || s.jitter || s.rollingShutter;
  }

  private get signalNeeded(): boolean {
    const s = this.settings;
    return s.exposure || s.grade || s.grain;
  }

  private get transmitNeeded(): boolean {
    return this.settings.blocking;
  }

  /** The target a pass writes into: the next ping-pong buffer, or the screen when it is last. */
  private nextTarget(remaining: number, chain: number): THREE.WebGLRenderTarget | null {
    return remaining === 0 ? null : this.chainTargets[chain];
  }

  private runPass(
    renderer: THREE.WebGLRenderer,
    material: THREE.ShaderMaterial,
    source: THREE.Texture,
    target: THREE.WebGLRenderTarget | null,
  ): THREE.Texture {
    material.uniforms['tDiffuse'].value = source;
    this.quad.material = material;
    renderer.setRenderTarget(target);
    renderer.render(this.quadScene, this.quadCamera);
    return target === null ? source : target.texture;
  }

  /**
   * Measure the scene's log-average luminance and read one byte back to the CPU.
   *
   * Rate-limited *and* read one interval late. `readRenderTargetPixels` is synchronous: asking for
   * a pixel the GPU has not finished writing stalls the pipeline, which showed up as a 4 ms spike
   * on every metering frame. Reading the result of the *previous* interval instead means the work
   * has long since completed, so the call returns immediately. The cost is 66 ms of extra latency
   * on a measurement feeding a 0.6 s lag, which is not a cost at all.
   */
  private meter(renderer: THREE.WebGLRenderer, sceneTarget: THREE.WebGLRenderTarget): void {
    if (this.meterAccumulatorS < 1 / POST.exposureSampleHz) return;
    this.meterAccumulatorS = 0;

    const write = this.meterSingleTargets[this.meterIndex];
    this.runPass(renderer, this.meterDownsampleMaterial, sceneTarget.texture, this.meterGridTarget);
    this.runPass(renderer, this.meterReduceMaterial, this.meterGridTarget.texture, write);

    // The very first measurement has nothing behind it, and the initial exposure snap needs a real
    // number rather than a default, so that one readback is taken immediately.
    const read = this.meterPrimed ? this.meterSingleTargets[this.meterIndex ^ 1] : write;
    renderer.readRenderTargetPixels(read, 0, 0, 1, 1, this.meterPixel);
    this.meterPrimed = true;
    this.meterIndex ^= 1;
    this.measuredLuminanceValue = decodeMeterByte(this.meterPixel[0]);

    // First measurement snaps. Adapting from a default would open every flight with a 0.6 s
    // brightness swing that has nothing to do with the scene.
    if (!this.exposureSettled) {
      this.exposureSettled = true;
      this.exposureStopsValue = targetExposureStops(this.measuredLuminanceValue);
    }
  }

  private updateCameraUniforms(camera: THREE.PerspectiveCamera): void {
    camera.matrixWorld.extractBasis(cameraRight, cameraUp, cameraForward);
    const optics = this.opticsMaterial.uniforms;
    (optics['uCameraRight'].value as THREE.Vector3).copy(cameraRight);
    (optics['uCameraUp'].value as THREE.Vector3).copy(cameraUp);
    // Three's camera looks down its local −Z; the basis column is +Z.
    (optics['uCameraForward'].value as THREE.Vector3).copy(cameraForward).negate();

    const tanHalfVertical = Math.tan((camera.fov * Math.PI) / 360);
    (optics['uTanHalfFov'].value as THREE.Vector2).set(
      tanHalfVertical * camera.aspect,
      tanHalfVertical,
    );
  }

  private createMaterial(
    fragmentShader: string,
    uniforms: Record<string, THREE.IUniform>,
  ): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      vertexShader: FULLSCREEN_VERTEX,
      fragmentShader,
      uniforms: { tDiffuse: { value: null }, ...uniforms },
      depthTest: false,
      depthWrite: false,
    });
  }

  /**
   * Half-float, because the sky writes a sun several times brighter than white and the exposure
   * shoulder needs those values intact to roll them off; an 8-bit target would have clipped them
   * to a flat disc before the stack ever saw them.
   *
   * The texture is marked sRGB so that Three applies exactly the conversion it would have applied
   * rendering straight to the canvas — without it, the scatter's lit materials would darken the
   * moment the stack was switched on, for reasons that would look like a lighting bug.
   */
  private createSceneTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
    });
    target.texture.colorSpace = THREE.SRGBColorSpace;
    target.texture.generateMipmaps = false;
    return target;
  }

  private createChainTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    target.texture.generateMipmaps = false;
    return target;
  }

  /** Metering targets are 8-bit on purpose: one byte is all the CPU reads back. */
  private createMeterTarget(sizePx: number): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(sizePx, sizePx, {
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    target.texture.generateMipmaps = false;
    return target;
  }
}
